import type { BridgeOrderFilters } from "~/lib/queries";

/**
 * Every filter on the bridge orders list, as URL search params.
 *
 * `customer_email` and `shopify_customer_id` are exact matches on the server,
 * against the plain btree indexes migration 015 added. `shopify_order_name` is
 * a prefix — it is the "#1042" a merchant quotes, where a prefix is useful.
 */
export interface BridgeSearch {
  status?: string;
  customer_email?: string;
  shopify_customer_id?: string;
  shopify_order_name?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

const FILTER_KEYS = [
  "status",
  "customer_email",
  "shopify_customer_id",
  "shopify_order_name",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof BridgeSearch)[];

export function validateBridgeSearch(raw: Record<string, unknown>): BridgeSearch {
  return {
    status: str(raw.status),
    customer_email: str(raw.customer_email),
    shopify_customer_id: str(raw.shopify_customer_id),
    shopify_order_name: str(raw.shopify_order_name),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

export function hasActiveFilters(search: BridgeSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

export function toFilters(search: BridgeSearch): BridgeOrderFilters {
  return {
    status: search.status,
    customer_email: search.customer_email,
    shopify_customer_id: search.shopify_customer_id,
    shopify_order_name: search.shopify_order_name,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
